import { defineComponent as _defineComponent } from 'vue'
import { computed, onUnmounted, ref, watch } from 'vue';
import { api } from '@/main';
import { Subscription } from '@/api';
import store from '@/store';
import Card from '@/components/Card.vue';
import Permissions from '@/components/Permissions.vue';
import TimecardPrintReport from './TimecardPrintReport.vue';
import {
  TimecardSnapshot, TimecardHolder, TimecardProject, TimecardEntry, TimecardReport,
  weekdays, localTime, utcOffset, localTimeOffsets, lastMonthRange, shiftDate, duration, entrySeconds, reportCsv, hoursToMinutes, suggestedBillingHours,
} from './timecard';


export default /*#__PURE__*/_defineComponent({
  __name: 'Timecard',
  props: ['household', 'timecardHolderId', 'view'],
  emits: ['breadcrumbs', 'menuItems'],
  setup(__props, { emit }) {

const props = __props



const data = ref<TimecardSnapshot | null>(null);
const error = ref('');
const busy = ref(false);
const now = ref(Date.now());
let clockOffset = 0;
let subscription: Subscription | null = null;
let disposed = false;
let refreshingWeek = false;
let entryRequest = 0;
let reportRequest = 0;
const page = computed(() => props.view || 'dashboard');
const holder = computed(() => data.value?.timecardHolder);
const zone = computed(() => holder.value?.timeZone || 'UTC');
const path = computed(() => `/h/${props.household.id}/timecard/${props.timecardHolderId}`);
const running = computed(() => data.value?.runningEntry || null);
const projects = computed(() => data.value?.projects || []);
const activeProjects = computed(() => projects.value.filter(project => project.active));
const disabled = computed(() => busy.value || store.state.connectionState !== 'connected');
const views = [
  { value: 'dashboard', title: 'Dashboard', icon: 'mdi-clock-outline' },
  { value: 'entries', title: 'Entries', icon: 'mdi-format-list-bulleted' },
  { value: 'reports', title: 'Reports', icon: 'mdi-file-chart-outline' },
  { value: 'settings', title: 'Settings', icon: 'mdi-cog' },
];
const url = (view: string) => path.value + (view === 'dashboard' ? '' : '/' + view);
const rpc = (method: string, args: object = {}) => api.rpc('timecard', method, { timecardHolderId: props.timecardHolderId, ...args });
const showError = (reason: unknown) => { error.value = reason instanceof Error ? reason.message : String(reason); };
const act = async (action: () => Promise<unknown>) => {
  if (busy.value) return;
  busy.value = true;
  error.value = '';
  try { await action(); } catch (reason) { showError(reason); } finally { busy.value = false; }
};

const entryStart = ref('');
const entryEnd = ref('');
const entries = ref<TimecardEntry[]>([]);
const entriesLoading = ref(false);
const reportStart = ref('');
const reportEnd = ref('');
const reportProject = ref<string | null>(null);
const selectedReportProject = computed(() => projects.value.find(project => project.id === reportProject.value));
const reportMaxHours = ref<string | number | null>('');
const reportMaxEdited = ref(false);
const defaultReportMaxHours = computed(() => suggestedBillingHours(projects.value.find(project => project.id === reportProject.value)));
const reportMaxError = computed(() => {
  try { hoursToMinutes(reportMaxHours.value); return ''; } catch (reason) { return reason instanceof Error ? reason.message : String(reason); }
});
function useDefaultReportMax() {
  reportMaxEdited.value = false;
  reportMaxHours.value = defaultReportMaxHours.value;
}
const report = ref<TimecardReport | null>(null);
const reportLoading = ref(false);
const settings = ref<TimecardHolder | null>(null);
const timeZones = ref<string[]>([]);
const timeZoneOptions = computed(() => Array.from(new Set(timeZones.value.concat(settings.value?.timeZone || []))));
const description = ref('');
const entryBillable = ref(true);
let descriptionEntryId: string | null = null;

const today = () => localTime(now.value, zone.value).slice(0, 10);
const formatTime = (instant: string) => localTime(instant, zone.value).replace('T', ' ');
const nameOf = (id: string) => projects.value.find(project => project.id === id)?.name || 'Project';
const weeklySeconds = (id: string | null) => {
  const snapshot = data.value;
  if (!snapshot) return 0;
  return snapshot.weekEntries.filter(entry => entry.timecardProjectId === id)
    .reduce((sum, entry) => sum + entrySeconds(entry, now.value, Date.parse(snapshot.weekStart), Date.parse(snapshot.weekEnd)), 0);
};
const totalWeeklySeconds = computed(() => projects.value.reduce((sum, project) => sum + weeklySeconds(project.id), 0));
const visibleTotals = computed(() => projects.value.filter(project => project.active || weeklySeconds(project.id) > 0));

function currentWeek() {
  if (!data.value) return;
  entryStart.value = localTime(data.value.weekStart, zone.value).slice(0, 10);
  entryEnd.value = shiftDate(entryStart.value, 6);
}
function lastMonth() {
  const range = lastMonthRange(today());
  reportStart.value = range.startDate;
  reportEnd.value = range.endDate;
}
function shiftWeek(direction: number) {
  if (!entryStart.value || !entryEnd.value) return;
  entryStart.value = shiftDate(entryStart.value, direction * 7);
  entryEnd.value = shiftDate(entryEnd.value, direction * 7);
}
function shiftMonth(direction: number) {
  if (!reportStart.value) return;
  const start = new Date(reportStart.value.slice(0, 7) + '-01T12:00:00Z');
  start.setUTCMonth(start.getUTCMonth() + direction);
  reportStart.value = start.toISOString().slice(0, 10);
  start.setUTCMonth(start.getUTCMonth() + 1);
  reportEnd.value = shiftDate(start.toISOString().slice(0, 10), -1);
}
async function loadEntries() {
  const request = ++entryRequest;
  entriesLoading.value = false;
  if (!data.value || page.value !== 'entries') return;
  entries.value = [];
  if (!entryStart.value || !entryEnd.value || entryStart.value > entryEnd.value) return;
  entriesLoading.value = true;
  try {
    const value = await rpc('getEntries', { startDate: entryStart.value, endDate: entryEnd.value });
    if (!disposed && request === entryRequest) entries.value = value;
  } catch (reason) { if (request === entryRequest) showError(reason); } finally { if (request === entryRequest) entriesLoading.value = false; }
}
async function loadReport() {
  const request = ++reportRequest;
  reportLoading.value = false;
  if (!data.value || page.value !== 'reports') return;
  report.value = null;
  if (!reportProject.value || !reportStart.value || !reportEnd.value || reportStart.value > reportEnd.value || reportMaxError.value) return;
  reportLoading.value = true;
  try {
    const value = await rpc('getReport', { startDate: reportStart.value, endDate: reportEnd.value, projectId: reportProject.value, billingMaxMinutes: hoursToMinutes(reportMaxHours.value) });
    if (!disposed && request === reportRequest) report.value = value;
  } catch (reason) { if (request === reportRequest) showError(reason); } finally { if (request === reportRequest) reportLoading.value = false; }
}
function navigation() {
  if (!holder.value) return;
  const menuItems = views.map(view => ({
    title: view.value === 'dashboard' ? holder.value?.name : view.title,
    icon: view.icon,
    to: url(view.value),
    exact: true,
    color: view.value === 'dashboard' ? '#1E3A8A' : undefined,
  }));
  emit('menuItems', menuItems);
  const breadcrumbs = [{ text: holder.value.name, to: path.value, icon: 'mdi-clock-outline' }];
  if (page.value !== 'dashboard') breadcrumbs.push({ text: views.find(view => view.value === page.value)?.title || 'Timecard', to: url(page.value), icon: '' });
  emit('breadcrumbs', breadcrumbs);
}
function receive(value: TimecardSnapshot) {
  if (!value || value.id !== props.timecardHolderId || disposed) return;
  const initial = !data.value;
  const oldDescription = running.value?.description || '';
  const oldBillable = running.value?.billable ?? true;
  value.projects = value.projects.map(project => ({
    ...project,
    weeklyTargetMinutes: project.weeklyTargetMinutes ?? null,
    weeklyMaxMinutes: project.weeklyMaxMinutes ?? null,
    reportDetails: project.reportDetails ?? null,
    sortOrder: project.sortOrder ?? null,
  }));
  data.value = value;
  clockOffset = Date.parse(value.serverNow) - Date.now();
  now.value = Date.now() + clockOffset;
  if (initial) { currentWeek(); lastMonth(); settings.value = { ...value.timecardHolder }; }
  const id = value.runningEntry?.id || null;
  const changedEntry = descriptionEntryId !== id;
  if (changedEntry || description.value === oldDescription) {
    description.value = value.runningEntry?.description || '';
    descriptionEntryId = id;
  }
  if (changedEntry || entryBillable.value === oldBillable) entryBillable.value = value.runningEntry?.billable ?? true;
  navigation();
  loadEntries();
  loadReport();
}
watch(() => props.timecardHolderId, id => {
  if (subscription) api.unsubscribe(subscription);
  entryRequest++; reportRequest++;
  data.value = null; entries.value = []; report.value = null;
  error.value = ''; settings.value = null; reportProject.value = null;
  reportMaxHours.value = ''; reportMaxEdited.value = false;
  description.value = ''; descriptionEntryId = null;
  entryBillable.value = true;
  entryDialog.value = false; projectDialog.value = false;
  subscription = api.subscribe('UITimecardHolder', id, receive);
});
watch(() => page.value, () => { navigation(); loadEntries(); loadReport(); });
watch([entryStart, entryEnd], loadEntries);
watch([reportStart, reportEnd, reportMaxHours], loadReport);
watch(reportProject, () => { useDefaultReportMax(); loadReport(); });
watch(defaultReportMaxHours, () => { if (!reportMaxEdited.value) useDefaultReportMax(); });
const tick = window.setInterval(async () => {
  now.value = Date.now() + clockOffset;
  if (data.value && now.value >= Date.parse(data.value.weekEnd) && !refreshingWeek && !disabled.value) {
    refreshingWeek = true;
    try { receive(await rpc('getTimecard')); } catch (reason) { showError(reason); } finally { refreshingWeek = false; }
  }
}, 250);
onUnmounted(() => {
  disposed = true;
  entryRequest++; reportRequest++;
  window.clearInterval(tick);
  if (subscription) api.unsubscribe(subscription);
});

const selectProject = (projectId: string | null) => act(async () => {
  if ((running.value?.timecardProjectId || null) === projectId) return;
  const current = running.value;
  const nextDescription = current ? null : description.value;
  const nextBillable = current ? true : entryBillable.value;
  if (current && (description.value !== (current.description || '') || entryBillable.value !== current.billable))
    await rpc('saveDescription', { entryId: current.id, version: current.version, description: description.value, billable: entryBillable.value });
  await rpc('selectProject', { projectId, description: nextDescription, billable: nextBillable });
});
const saveDescription = () => act(async () => {
  const entry = running.value;
  if (entry) await rpc('saveDescription', { entryId: entry.id, version: entry.version, description: description.value, billable: entryBillable.value });
});

const projectDialog = ref(false);
const projectDraft = ref<TimecardProject | null>(null);
const targetHours = ref<string | number>('');
const maxHours = ref<string | number>('');
function editProject(project?: TimecardProject) {
  error.value = '';
  projectDraft.value = project
    ? { ...project }
    : {
      id: null,
      version: null,
      timecardHolderId: props.timecardHolderId,
      name: '',
      active: true,
      weeklyTargetMinutes: null,
      weeklyMaxMinutes: null,
      sortOrder: null,
      reportDetails: null,
      createdTimestamp: null,
      deletedTimestamp: null,
    };
  targetHours.value = project?.weeklyTargetMinutes == null ? '' : project.weeklyTargetMinutes / 60;
  maxHours.value = project?.weeklyMaxMinutes == null ? '' : project.weeklyMaxMinutes / 60;
  projectDialog.value = true;
}
const saveProject = () => act(async () => {
  if (!projectDraft.value) return;
  await rpc('saveProject', { project: { ...projectDraft.value, weeklyTargetMinutes: hoursToMinutes(targetHours.value), weeklyMaxMinutes: hoursToMinutes(maxHours.value) } });
  projectDialog.value = false;
});
const saveSettings = () => act(async () => {
  settings.value = await rpc('saveTimecardHolder', { timecardHolder: settings.value });
});

const entryDialog = ref(false);
const entryDraft = ref({
  entryId: null as string | null,
  version: null as number | null,
  projectId: '',
  startLocal: '',
  stopLocal: '',
  startOffset: '',
  stopOffset: '',
  description: '',
  billable: true,
});
const offsetOptions = (value: string) => localTimeOffsets(value, zone.value).map((offset, index) => ({
  value: offset,
  text: `${index === 0 ? 'First' : 'Second'} occurrence (UTC${offset})`,
}));
const startOffsetOptions = computed(() => entryDialog.value ? offsetOptions(entryDraft.value.startLocal) : []);
const stopOffsetOptions = computed(() => entryDialog.value ? offsetOptions(entryDraft.value.stopLocal) : []);
function editEntry(entry?: TimecardEntry) {
  error.value = '';
  const start = entry?.startTimestamp || new Date(now.value - 3600000).toISOString();
  const stop = entry ? entry.stopTimestamp : new Date(now.value).toISOString();
  entryDraft.value = {
    entryId: entry?.id || null,
    version: entry?.version || null,
    projectId: entry?.timecardProjectId || activeProjects.value[0]?.id || projects.value[0]?.id || '',
    startLocal: localTime(start, zone.value),
    stopLocal: stop ? localTime(stop, zone.value) : '',
    startOffset: utcOffset(start, zone.value),
    stopOffset: stop ? utcOffset(stop, zone.value) : '',
    description: entry?.description || '',
    billable: entry?.billable ?? true,
  };
  entryDialog.value = true;
}
const saveEntry = () => act(async () => {
  await rpc('saveEntry', { ...entryDraft.value, stopLocal: entryDraft.value.stopLocal || null });
  entryDialog.value = false;
});
const deleteEntry = () => act(async () => {
  await rpc('deleteEntry', { entryId: entryDraft.value.entryId, version: entryDraft.value.version });
  entryDialog.value = false;
});
function exportReport() {
  const project = selectedReportProject.value;
  if (!report.value || reportLoading.value || !project?.id) return;
  const blob = new Blob([reportCsv(report.value, project)], { type: 'text/csv;charset=utf-8;' });
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  const projectName = project.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '') || project.id;
  link.download = `timecard-${projectName}-${report.value.startDate}-${report.value.endDate}${report.value.includesRunningEntry ? '-draft' : ''}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}
const entryHeaders = [
  { text: 'Start', value: 'startTimestamp' }, { text: 'Stop', value: 'stopTimestamp' },
  { text: 'Project', value: 'timecardProjectId' }, { text: 'Duration', value: 'duration', sortable: false },
  { text: 'Billable', value: 'billable' },
  { text: 'Description', value: 'description', sortable: false }, { text: '', value: 'actions', sortable: false },
];
// Initialize only after all dialog refs exist; callbacks can be delivered immediately.
subscription = api.subscribe('UITimecardHolder', props.timecardHolderId, receive);
api.rpc('timecard', 'getTimeZones', {}).then((zones: string[]) => { timeZones.value = zones; }).catch(showError);

return { __sfc: true,props, emit, data, error, busy, now, clockOffset, subscription, disposed, refreshingWeek, entryRequest, reportRequest, page, holder, zone, path, running, projects, activeProjects, disabled, views, url, rpc, showError, act, entryStart, entryEnd, entries, entriesLoading, reportStart, reportEnd, reportProject, selectedReportProject, reportMaxHours, reportMaxEdited, defaultReportMaxHours, reportMaxError, useDefaultReportMax, report, reportLoading, settings, timeZones, timeZoneOptions, description, entryBillable, descriptionEntryId, today, formatTime, nameOf, weeklySeconds, totalWeeklySeconds, visibleTotals, currentWeek, lastMonth, shiftWeek, shiftMonth, loadEntries, loadReport, navigation, receive, tick, selectProject, saveDescription, projectDialog, projectDraft, targetHours, maxHours, editProject, saveProject, saveSettings, entryDialog, entryDraft, offsetOptions, startOffsetOptions, stopOffsetOptions, editEntry, saveEntry, deleteEntry, exportReport, entryHeaders, Card, Permissions, TimecardPrintReport, weekdays, duration, entrySeconds }
}

})